import { useCallback, useEffect, useState, createContext, useContext } from "react";
import api from "./api";

/**
 * Per-team subscription / plan state.
 *
 * Backend: GET /api/teams/{id}/subscription returns
 *   { plan, plan_info, status, current_period_end, trial_end,
 *     admin_bypass?, usage }
 *
 * Components consume this via `usePlan()` to gate UI and show upgrade banners.
 */
const PlanContext = createContext({
  loading: true,
  plan: "free",
  info: null,
  status: "free",
  features: {},
  trialEnd: null,
  periodEnd: null,
  adminBypass: false,
  reload: () => {},
});

const fallback = { features: {} };

export function PlanProvider({ teamId, children }) {
  const [state, setState] = useState({ loading: true, ...fallback });

  const reload = useCallback(async () => {
    if (!teamId) {
      setState({ loading: false, ...fallback, plan: "free" });
      return;
    }
    try {
      const { data } = await api.get(`/teams/${teamId}/subscription`);
      setState({
        loading: false,
        plan: data.plan,
        info: data.plan_info,
        status: data.status,
        features: data.plan_info?.features || {},
        trialEnd: data.trial_end,
        periodEnd: data.current_period_end,
        cancelAtPeriodEnd: data.cancel_at_period_end,
        adminBypass: !!data.admin_bypass,
        usage: data.usage,
        daysUntilExpiry: data.days_until_expiry,
      });
    } catch (e) {
      setState({ loading: false, ...fallback, plan: "free" });
    }
  }, [teamId]);

  useEffect(() => { reload(); }, [reload]);

  const has = useCallback((feature) => {
    if (state.adminBypass) return true;
    return !!state.features[feature];
  }, [state.adminBypass, state.features]);

  return (
    <PlanContext.Provider value={{ ...state, has, reload }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  return useContext(PlanContext);
}
